/**
 * sendmoments header (sections/sm-header.liquid): desktop mega menu, mobile/tablet drawer, search overlay.
 * All markup is server-rendered; this element only switches it. Port of the SMUK navigation prototype's
 * controllers, keeping its timings and its hover-intent rules.
 */

// Mirror the CSS durations (--sm-nav-dur-panel, --sm-nav-drawer-dur, --sm-nav-search-dur).
const PANEL_EXIT_MS = 160;
const DRAWER_MS = 280;
const SEARCH_MS = 200;
// Grace after the pointer leaves the nav, so a fast diagonal clipping outside for a frame keeps the panel.
const LEAVE_MS = 120;
const RECENTS_KEY = 'sm_recent_searches';
const RECENTS_MAX = 5;
const DESKTOP = '(min-width: 64rem)';
const TABLET = '(min-width: 40rem) and (max-width: 62rem)';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SmNav extends HTMLElement {
  #abort;
  #resizeObserver;
  #timers = new Map();
  #active = null;
  #deliberate = false;
  #lastOut = null;
  #overPaired = false;
  #pointerType = null;
  #scrollHidden = false;
  #lastY = 0;
  #panel = null;
  #displayed = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const on = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: this.#abort.signal });

    this.header = this.closest('.shopify-section') ?? this;
    this.tabs = [...this.querySelectorAll('[data-sm-nav-tab]')];
    this.mega = this.querySelector('[data-sm-nav-mega]');
    this.backdrop = this.querySelector('[data-sm-nav-backdrop]');
    this.burger = this.querySelector('[data-sm-nav-open-drawer]');
    this.drawer = this.querySelector('[data-sm-nav-drawer]');
    this.search = this.querySelector('[data-sm-nav-search]');
    this.profile = this.querySelector('[data-sm-nav-profile]');
    this.profilePanel = this.querySelector('[data-sm-nav-profile-panel]');

    this.#bindMegaMenu(on);
    this.#bindDrawer(on);
    this.#bindSearch(on);
    this.#bindProfile(on);

    on(window, 'scroll', () => this.#onScroll(), { passive: true });
    // bfcache restores the page as it was left: close every layer, whatever link or form left it.
    on(window, 'pagehide', () => {
      this.#setActive(null, { instant: true });
      if (this.drawer.open) this.drawer.close();
      if (this.search.open) this.search.close();
      this.profilePanel?.hidePopover?.();
    });
    // Geometry changes (breakpoint crossings included) are handled in #syncHeader via the
    // ResizeObserver — the 64rem cut alone lies when six tabs keep the burger to 71.94rem.
    on(window.matchMedia(DESKTOP), 'change', () => this.#syncHeader());
    this.#resizeObserver = new ResizeObserver(() => this.#syncHeader());
    this.#resizeObserver.observe(this.header);
    this.#lastY = window.scrollY;
    this.#syncHeader();
  }

  disconnectedCallback() {
    // A dialog removed while open fires no `close`; the scroll lock is CSS (:has) and releases with it.
    this.#abort.abort();
    this.#resizeObserver?.disconnect();
    for (const timer of this.#timers.values()) clearTimeout(timer);
    this.#timers.clear();
  }

  /* ---------- desktop mega menu ---------- */

  #bindMegaMenu(on) {
    on(document, 'pointerover', (event) => (this.#pointerType = event.pointerType), {
      capture: true,
    });
    // Only a pointer that really moved counts as entering or leaving. Content vanishing under a still pointer
    // (drawer closing, panel switching) makes the browser report an entry without the paired mouseout.
    on(document, 'mouseout', (event) => (this.#lastOut = event), { capture: true });
    on(
      document,
      'mouseover',
      (event) => {
        const out = this.#lastOut;
        this.#overPaired =
          Boolean(out) && out.target === event.relatedTarget && out.relatedTarget === event.target;
        this.#lastOut = null;
      },
      { capture: true }
    );

    for (const tab of this.tabs) {
      const handle = tab.dataset.smNavTab;
      on(tab, 'mouseenter', (event) => {
        // Only a mouse hovers. A tap fires mouse events too, and iPadOS drops its click when a hover shows content.
        if (this.#pointerType !== 'mouse' || !this.#pointerMovedIn(event)) return;
        this.#clearTimer('leave');
        // Re-entering the open tab must not rewrite how it was opened, or a click-opened panel stops toggling.
        if (this.#active === handle) return;
        this.#deliberate = false;
        this.#setActive(handle);
      });
      on(tab, 'click', () => this.#toggleTab(handle));
      on(tab, 'keydown', (event) => this.#onTabKeydown(event, handle));
    }

    // mouseover, not mouseenter: the backdrop is inside <sm-nav>, so returning from it enters nothing.
    on(this, 'mouseover', (event) => {
      if (!this.backdrop.contains(event.target) && this.#pointerMovedIn(event))
        this.#clearTimer('leave');
    });
    on(this, 'mouseleave', (event) => {
      if (this.#pointerMovedOut(event)) this.#scheduleHoverClose();
    });
    on(this.backdrop, 'mouseenter', (event) => {
      if (this.#pointerMovedIn(event)) this.#scheduleHoverClose();
    });
    on(this.backdrop, 'click', () => this.#setActive(null));
    on(this.mega, 'keydown', (event) => this.#onPanelKeydown(event));
    // Focus leaving the header must not leave the panel open over the element that now has it.
    on(this, 'focusout', (event) => {
      if (this.#active && event.relatedTarget && !this.contains(event.relatedTarget))
        this.#setActive(null);
    });
    on(document, 'pointerdown', (event) => {
      if (this.#active && !this.contains(event.target)) this.#setActive(null);
    });
    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !this.#active) return;
      const tab = this.#tab(this.#active);
      // Pull focus back only from inside the header: a hover-opened panel says nothing about focus.
      const hadFocus = this.contains(document.activeElement);
      this.#setActive(null);
      if (hadFocus) tab?.focus();
    });

    // Theme Editor: selecting a tab block opens its panel.
    on(document, 'shopify:block:select', (event) => {
      const panel = event.target.closest?.('.sm-nav-panel');
      if (!panel || !this.mega.contains(panel)) return;
      this.#deliberate = true;
      this.#setActive(panel.id.slice(this.mega.id.length + 1));
    });
    on(document, 'shopify:block:deselect', (event) => {
      if (this.mega.contains(event.target)) this.#setActive(null);
    });
  }

  #pointerMovedIn(event) {
    return !event.relatedTarget || this.#overPaired;
  }

  #pointerMovedOut(event) {
    return Boolean(this.#lastOut) && this.#lastOut.relatedTarget === event.relatedTarget;
  }

  // Hover opens; only a panel opened by click, Enter/Space or ArrowDown closes on the next activation.
  #toggleTab(handle) {
    const closing = this.#active === handle && this.#deliberate;
    this.#deliberate = !closing;
    this.#setActive(closing ? null : handle);
  }

  // Hover-opened panels follow the pointer out; a deliberately opened one waits for a deliberate dismissal.
  #scheduleHoverClose() {
    if (this.#deliberate || !this.#active) return;
    this.#setTimer('leave', () => this.#setActive(null), LEAVE_MS);
  }

  #onTabKeydown(event, handle) {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowLeft': {
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const index = this.tabs.findIndex((tab) => tab.dataset.smNavTab === handle);
        const next = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
        // Menubar convention: arrowing carries an open panel along, a closed row stays closed.
        if (this.#active) this.#setActive(next.dataset.smNavTab);
        next.focus();
        break;
      }
      case 'ArrowDown':
        event.preventDefault();
        // Deliberate before it opens: the backdrop appearing under a still pointer would otherwise hover-close it.
        this.#deliberate = true;
        this.#setActive(handle);
        this.#panelLinks(handle)[0]?.focus();
        break;
      case 'Tab': {
        // The panels sit after all the tabs: Tab from the open tab enters its panel.
        const first =
          this.#active === handle && !event.shiftKey ? this.#panelLinks(handle)[0] : null;
        if (!first) break;
        event.preventDefault();
        this.#deliberate = true;
        first.focus();
        break;
      }
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#toggleTab(handle);
        break;
    }
  }

  // As if the panel followed its tab: Shift+Tab from the first link returns to the tab,
  // Tab from the last moves on to the next tab (or the icons) and closes the panel.
  #onPanelKeydown(event) {
    if (event.key !== 'Tab' || !this.#active) return;
    const links = this.#panelLinks(this.#active);
    const tab = this.#tab(this.#active);
    if (event.shiftKey) {
      if (event.target !== links[0]) return;
      event.preventDefault();
      tab.focus();
      return;
    }
    if (event.target !== links.at(-1)) return;
    event.preventDefault();
    const next =
      this.tabs[this.tabs.indexOf(tab) + 1] ?? this.querySelector('[data-sm-nav-open-search]');
    next.focus();
    this.#setActive(null);
  }

  #setActive(handle, { instant = false } = {}) {
    this.#active = handle;
    this.#clearTimer('leave');
    this.#clearTimer('panelExit');
    for (const tab of this.tabs) {
      const on = tab.dataset.smNavTab === handle;
      tab.classList.toggle('sm-nav__tab--active', on);
      tab.setAttribute('aria-expanded', String(on));
    }
    this.#syncHeader();

    if (handle) {
      // The shell stays mounted across tab switches, so its entrance plays once per open, not per tab.
      this.mega.classList.remove('sm-nav__mega--closing');
      this.backdrop.classList.remove('sm-nav__backdrop--closing');
      for (const panel of this.#panels()) {
        panel.hidden = panel !== this.#panelFor(handle);
        panel.inert = false;
      }
      this.mega.hidden = false;
      this.backdrop.hidden = false;
      return;
    }

    if (this.mega.hidden) return;
    for (const panel of this.#panels()) panel.inert = true;
    if (instant || reducedMotion()) return this.#hideMega();
    this.mega.classList.add('sm-nav__mega--closing');
    this.backdrop.classList.add('sm-nav__backdrop--closing');
    this.#setTimer('panelExit', () => this.#hideMega(), PANEL_EXIT_MS);
  }

  #hideMega() {
    this.mega.hidden = true;
    this.backdrop.hidden = true;
    this.mega.classList.remove('sm-nav__mega--closing');
    this.backdrop.classList.remove('sm-nav__backdrop--closing');
    for (const panel of this.#panels()) panel.hidden = true;
  }

  #panels() {
    return this.mega.querySelectorAll(':scope > .sm-nav-panel');
  }

  #panelLinks(handle) {
    return [...(this.#panelFor(handle)?.querySelectorAll('a[href]') ?? [])];
  }

  #panelFor(handle) {
    return document.getElementById(`${this.mega.id}-${handle}`);
  }

  #tab(handle) {
    return this.tabs.find((tab) => tab.dataset.smNavTab === handle);
  }

  /* ---------- mobile / tablet drawer ---------- */

  #bindDrawer(on) {
    const drawer = this.drawer;
    this.drawerRoot = drawer.querySelector('[data-sm-nav-drawer-root]');
    this.drawerDetail = drawer.querySelector('[data-sm-nav-drawer-detail]');
    this.drawerBack = drawer.querySelector('[data-sm-nav-drawer-back]');
    this.drawerTablet = drawer.querySelector('[data-sm-nav-drawer-tablet]');
    this.drawerBodies = [...drawer.querySelectorAll('[data-sm-nav-drawer-body]')];

    on(this.burger, 'click', () => this.#openDrawer());
    for (const chip of this.querySelectorAll('[data-sm-nav-chip]')) {
      on(chip, 'click', () => this.#openDrawer(chip.dataset.smNavChip));
    }

    on(drawer, 'click', (event) => {
      const target = event.target;
      if (target.closest('a[href]')) {
        // Close at once: pagehide covers leaving the page, not a same-page #link.
        drawer.close();
        return;
      }
      if (target.closest('[data-sm-nav-drawer-close]')) return this.#closeDrawer();
      if (target.closest('[data-sm-nav-drawer-back]')) return this.#setDrawerPanel(null);
      if (target.closest('[data-sm-nav-drawer-search]')) {
        drawer.close();
        this.#openSearch();
        return;
      }
      const panelButton = target.closest('[data-sm-nav-drawer-panel]');
      if (panelButton) this.#setDrawerPanel(panelButton.dataset.smNavDrawerPanel);
    });
    // Escape and Android back: our own animated close. stopPropagation keeps the theme's global
    // key:escape (dialog.js closes any open dialog after 100ms) from cutting the animation short.
    on(drawer, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.#closeDrawer();
    });
    on(drawer, 'cancel', (event) => {
      event.preventDefault();
      this.#closeDrawer();
    });
    on(drawer, 'close', () => {
      this.#clearTimer('drawerClose');
      // A link closes the drawer at once; a Back slide still pending would blank level 2 on the next open.
      this.#clearTimer('drawerDisplay');
      drawer.classList.remove('sm-nav-drawer--closing');
      this.burger.setAttribute('aria-expanded', 'false');
      this.#syncHeader();
    });
  }

  #openDrawer(handle = null) {
    const drawer = this.drawer;
    const target =
      handle ??
      (window.matchMedia(TABLET).matches ? this.drawerBodies[0]?.dataset.smNavDrawerBody : null);
    this.#clearTimer('drawerClose');
    drawer.classList.remove('sm-nav-drawer--closing');
    if (drawer.open) {
      this.#setDrawerPanel(target);
      return;
    }
    // Positioned while still display:none, so the levels land in place without a slide on open.
    this.#panel = target;
    this.#syncDrawerLevels();
    this.#setDisplayed(target);
    drawer.showModal();
    this.burger.setAttribute('aria-expanded', 'true');
    this.#syncHeader();
  }

  #closeDrawer() {
    const drawer = this.drawer;
    if (!drawer.open || drawer.classList.contains('sm-nav-drawer--closing')) return;
    if (reducedMotion()) return drawer.close();
    drawer.classList.add('sm-nav-drawer--closing');
    this.#setTimer('drawerClose', () => drawer.close(), DRAWER_MS);
  }

  // Level position follows `panel` at once; the content follows `displayed`, which lags on the way back so
  // level 2 slides out with its content still on it.
  #setDrawerPanel(handle) {
    if (this.#panel === handle) return;
    const previous = this.#panel;
    // Focus in the level turning inert, or on Back (hidden after the slide), would drop to <body>.
    const focused = document.activeElement;
    const fromRoot = this.drawerRoot.contains(focused);
    const fromDetail = this.drawerDetail.contains(focused) || focused === this.drawerBack;
    this.#panel = handle;
    this.#syncDrawerLevels();
    this.#clearTimer('drawerDisplay');
    if (handle) {
      this.#setDisplayed(handle);
      if (fromRoot) this.drawerBack.focus();
      return;
    }
    // preventScroll: the row is mid-slide, and scrolling to it would shift the clipped viewport sideways.
    if (fromDetail) {
      this.drawerRoot
        .querySelector(`[data-sm-nav-drawer-panel="${previous}"]`)
        ?.focus({ preventScroll: true });
    }
    this.#setTimer(
      'drawerDisplay',
      () => this.#setDisplayed(null),
      reducedMotion() ? 0 : DRAWER_MS
    );
  }

  #syncDrawerLevels() {
    const pushed = Boolean(this.#panel);
    this.drawerRoot.classList.toggle('sm-nav-drawer__level--pushed', pushed);
    this.drawerDetail.classList.toggle('sm-nav-drawer__level--open', pushed);
    // inert also takes the hidden level out of the accessibility tree.
    this.drawerRoot.inert = pushed;
    this.drawerDetail.inert = !pushed;
    for (const button of this.drawerTablet.querySelectorAll('[data-sm-nav-drawer-panel]')) {
      if (button.dataset.smNavDrawerPanel === this.#panel)
        button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  }

  #setDisplayed(handle) {
    if (this.#displayed === handle) return;
    this.#displayed = handle;
    for (const body of this.drawerBodies) {
      const shown = body.dataset.smNavDrawerBody === handle;
      // Each category opens collapsed, as the prototype reset its sections per category.
      if (shown) for (const details of body.querySelectorAll('details[open]')) details.open = false;
      body.hidden = !shown;
    }
    this.drawerBack.hidden = !handle;
    this.drawerTablet.hidden = !handle;
  }

  /* ---------- search overlay ---------- */

  #bindSearch(on) {
    const search = this.search;
    this.searchField = search.querySelector('search-input');
    this.searchForm = search.querySelector('form');
    this.searchInput = search.querySelector('input[type="search"]');
    this.recentsGroup = search.querySelector('[data-sm-nav-recents]');
    this.recentsList = search.querySelector('[data-sm-nav-recents-list]');
    this.recentTemplate = search.querySelector('[data-sm-nav-recent-template]');

    for (const trigger of this.querySelectorAll('[data-sm-nav-open-search]')) {
      on(trigger, 'click', (event) => {
        event.preventDefault();
        this.#openSearch();
      });
    }
    on(search, 'click', (event) => {
      const remove = event.target.closest('[data-sm-nav-remove]');
      if (remove) {
        this.#writeRecents(
          this.#readRecents().filter((term) => term !== remove.dataset.smNavRemove)
        );
        this.#renderRecents();
        this.searchInput.focus();
        return;
      }
      const term = event.target.closest('[data-sm-nav-term]');
      if (term) return this.#addRecent(term.dataset.smNavTerm);
      if (event.target.closest('[data-sm-nav-search-close]')) this.#closeSearch();
    });
    on(this.searchForm, 'submit', (event) => {
      const term = this.searchInput.value.trim();
      if (!term) {
        event.preventDefault();
        return;
      }
      this.#addRecent(term);
    });
    on(search, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this.#closeSearch();
    });
    on(search, 'cancel', (event) => {
      event.preventDefault();
      this.#closeSearch();
    });
    on(search, 'close', () => {
      this.#clearTimer('searchClose');
      search.classList.remove('sm-nav-search--closing');
      this.#syncHeader();
    });
  }

  #openSearch() {
    this.#setActive(null);
    this.#clearTimer('searchClose');
    this.search.classList.remove('sm-nav-search--closing');
    this.#renderRecents();
    if (!this.search.open) this.search.showModal();
    // Clears the query and the predictive results left from the last visit, and focuses the input.
    if (this.searchField?.clearSearchInput) {
      this.searchField.clearSearchInput();
    } else {
      this.searchInput.value = '';
      this.searchInput.focus();
    }
    this.#syncHeader();
  }

  #closeSearch() {
    const search = this.search;
    if (!search.open || search.classList.contains('sm-nav-search--closing')) return;
    if (reducedMotion()) return search.close();
    search.classList.add('sm-nav-search--closing');
    this.#setTimer('searchClose', () => search.close(), SEARCH_MS);
  }

  #readRecents() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]');
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string') : [];
    } catch {
      return [];
    }
  }

  #writeRecents(terms) {
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(terms));
    } catch {
      // Storage blocked (private mode): recents simply are not kept.
    }
  }

  #addRecent(term) {
    const value = term.trim();
    if (!value) return;
    const others = this.#readRecents().filter((existing) => existing !== value);
    this.#writeRecents([value, ...others].slice(0, RECENTS_MAX));
  }

  #renderRecents() {
    const terms = this.#readRecents();
    const action = this.searchForm.getAttribute('action');
    const removeLabel = this.recentsList.dataset.removeLabel ?? '';
    const chips = [];
    for (const term of terms) {
      const chip = this.recentTemplate.content.firstElementChild.cloneNode(true);
      const link = chip.querySelector('[data-sm-nav-term]');
      const remove = chip.querySelector('[data-sm-nav-remove]');
      link.textContent = term;
      link.href = `${action}?q=${encodeURIComponent(term)}&type=product`;
      link.dataset.smNavTerm = term;
      remove.dataset.smNavRemove = term;
      remove.setAttribute(
        'aria-label',
        removeLabel.replace('__TERM__', () => term)
      );
      chips.push(chip);
    }
    this.recentsList.replaceChildren(...chips);
    this.recentsGroup.hidden = chips.length === 0;
  }

  /* ---------- phone profile panel (account + My designs) ---------- */

  // The popover handles open, close, Esc and outside taps; it sits in the top layer, so it is placed here.
  #bindProfile(on) {
    if (!this.profilePanel) return;
    on(this.profilePanel, 'beforetoggle', (event) => {
      if (event.newState !== 'open') return;
      const icon = this.profile.getBoundingClientRect();
      this.profilePanel.style.top = `${icon.bottom + 4}px`;
      this.profilePanel.style.right = `${document.documentElement.clientWidth - icon.right}px`;
    });
  }

  /* ---------- header: hide on scroll, height variables ---------- */

  #onScroll() {
    // Placed against the icon when it opened: once the page moves, the header may hide or shift under it.
    this.profilePanel?.hidePopover?.();
    // The desktop header scrolls away but the backdrop is fixed: close the panel once it is out of view.
    if (this.#active && this.mega.getBoundingClientRect().bottom <= 0) this.#setActive(null);
    const y = window.scrollY;
    const delta = y - this.#lastY;
    if (Math.abs(delta) < 8) return;
    this.#scrollHidden = y > 72 && delta > 0;
    this.#lastY = y;
    this.#syncHeader();
  }

  // Hide-on-scroll is a phone/tablet behaviour (the desktop header is not sticky); an open menu, drawer or
  // search keeps the header in view. --header-height feeds sticky offsets (cart summary, anchors),
  // --header-h the PLP filter bar; header-element.js set the first one before this header existed.
  #syncHeader() {
    // Close the drawer only once the burger is really gone (six tabs keep it past the 64rem
    // desktop cut, to 71.94rem — NGE-14918), never while it is still the only navigation.
    if (this.drawer.open && this.burger.offsetParent === null) this.drawer.close();
    // The profile panel is placed for the geometry it opened in; any header resize
    // (rotation, split-screen) invalidates those fixed coords, so close it.
    this.profilePanel?.hidePopover?.();
    const sticky = !this.#isDesktop();
    const hidden =
      sticky && this.#scrollHidden && !this.#active && !this.drawer.open && !this.search.open;
    this.header.classList.toggle('header-hidden', hidden);
    const height = sticky ? Math.round(this.header.getBoundingClientRect().height) : 0;
    const style = document.documentElement.style;
    style.setProperty('--header-height', `${height}px`);
    style.setProperty('--header-h', `${hidden ? 0 : height}px`);
  }

  #isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  #setTimer(name, callback, delay) {
    this.#clearTimer(name);
    this.#timers.set(
      name,
      setTimeout(() => {
        this.#timers.delete(name);
        callback();
      }, delay)
    );
  }

  #clearTimer(name) {
    clearTimeout(this.#timers.get(name));
    this.#timers.delete(name);
  }
}

if (!customElements.get('sm-nav')) customElements.define('sm-nav', SmNav);
